import { CommentWatcherArtifactPanel } from '@/spaces/chat/panels/CommentWatcherArtifactPanel.tsx';
import { CustomMonitorPanel } from '@/spaces/chat/panels/CustomMonitorPanel.tsx';
import { DigitalAdArtifactPanel } from '@/spaces/chat/panels/DigitalAdArtifactPanel.tsx';
import { EmailArtifactPanel } from '@/spaces/chat/panels/EmailArtifactPanel.tsx';
import { GenericArtifactPanel } from '@/spaces/chat/panels/GenericArtifactPanel.tsx';
import { IGCommentReplyArtifactPanel } from '@/spaces/chat/panels/IGCommentReplyArtifactPanel.tsx';
import { PlaintextEmailArtifactPanel } from '@/spaces/chat/panels/PlaintextEmailArtifactPanel.tsx';
import { PostArtifactPanel } from '@/spaces/chat/panels/PostArtifactPanel.tsx';
import { SubredditMonitorPanel } from '@/spaces/chat/panels/SubredditMonitorPanel.tsx';

import {
	isCustomMonitor,
	isDigitalAd,
	isEmailHtml,
	isEmailPlaintext,
	isIGCommentReply,
	isIGCommentWatcherClass,
	isPost,
	isSubredditMonitor,
} from '../../../../../bridge/types/artifact.ts';
import type { ArtifactChatPanel } from '../../../../../bridge/types/state.ts';

import { ChatPanelLayout } from './ChatPanelLayout.tsx';

interface ArtifactPanelProps {
	panel: ArtifactChatPanel;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const ArtifactPanel = ({
	panel,
	onBack,
	onClose,
	style,
}: ArtifactPanelProps) => {
	if (panel.status === 'loading') {
		return (
			<ChatPanelLayout
				title="Edit Draft"
				isLoading
				onBack={onBack}
				onClose={onClose}
				style={style}
			>
				<div />
			</ChatPanelLayout>
		);
	}

	const artifact = panel.artifact;
	if (panel.status === 'error' || !artifact) {
		return (
			<ChatPanelLayout
				title="Edit Draft"
				onBack={onBack}
				onClose={onClose}
				style={style}
			>
				<div className="flex flex-1 items-center justify-center p-4">
					<p className="text-(--text-primary)">
						{panel.status === 'error'
							? `Failed to load content${panel.error ? `: ${panel.error}` : '.'}`
							: 'Content unavailable'}
					</p>
				</div>
			</ChatPanelLayout>
		);
	}

	if (isPost(artifact)) {
		return (
			<PostArtifactPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isDigitalAd(artifact)) {
		return (
			<DigitalAdArtifactPanel
				artifact={artifact}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isEmailPlaintext(artifact)) {
		return (
			<PlaintextEmailArtifactPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isEmailHtml(artifact)) {
		return (
			<EmailArtifactPanel
				artifact={artifact}
				stepIndex={panel.step_index ?? 0}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isIGCommentWatcherClass(artifact)) {
		return (
			<CommentWatcherArtifactPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isSubredditMonitor(artifact)) {
		return (
			<SubredditMonitorPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isCustomMonitor(artifact)) {
		return (
			<CustomMonitorPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	if (isIGCommentReply(artifact)) {
		return (
			<IGCommentReplyArtifactPanel
				artifact={artifact}
				onBack={onBack}
				onClose={onClose}
				style={style}
			/>
		);
	}

	return (
		<GenericArtifactPanel
			artifact={artifact}
			onBack={onBack}
			onClose={onClose}
			style={style}
		/>
	);
};
